import { ReactElement } from 'react'
import ReactLoading from 'react-loading'
import styled, { keyframes, css } from 'styled-components'
import { Position, Tooltip } from 'react-tippy'

import { detectIE } from '../../utils/functions'
import SeoTips from '../seoTips'
import { palette } from '../../styles/variables'
import { usePrevious } from '../../utils/hooks'
import { fadeOut } from '../../styles/animations'

import { LoadingText } from './LoadingText'

const rotation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(359deg) }
`
const LoadingWrapper = styled.div(
  ({ paddingTop = '15%', paddingBottom = '15%' }) => ({
    paddingTop,
    paddingBottom,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '& > div:last-child': {
      animation:
        detectIE() >= 12 ? `${css(rotation)} 1s infinite linear` : 'none',
    },
  }),
)

const ResponsiveWrapper = styled.div(({ height }: { height: string }) => ({
  width: '100%',
  height,
  position: 'relative',
  overflow: 'hidden',
}))

const ResponsiveContainer = styled.div(
  ({ size, offsetY }: { size: string; offsetY: string }) => ({
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: `calc(50% - ${offsetY} - ${size} / 2)`,
    left: `calc(50% - ${size} / 2)`,
  }),
)

const TooltipLoader = styled.div`
  &.fadeOut {
    animation: ${fadeOut} 0.5s linear forwards;
  }
  &.hidden {
    visibility: hidden;
  }
`

const getClassName = (prevShow: boolean | undefined, show: boolean) => {
  if (show) return ''

  return prevShow ? 'fadeOut' : 'hidden'
}

interface TooltipLoadingIndicatorProps {
  show?: boolean
  tooltipContent?: ReactElement
  popperOptions?: Record<string, unknown>
  position?: Position
  width?: number
  height?: number
  color?: string
}

export const TooltipLoadingIndicator = ({
  show = false,
  tooltipContent,
  popperOptions = {},
  position = 'left',
  width = 20,
  height = 20,
  color = palette.orange,
}: TooltipLoadingIndicatorProps) => {
  const prevShow = usePrevious(show)

  return (
    <TooltipLoader className={getClassName(prevShow, show)}>
      {/* @ts-ignore */}
      <Tooltip
        arrow
        position={position}
        html={tooltipContent}
        popperOptions={popperOptions}
      >
        <LoadingWheel width={width} height={height} color={color} />
      </Tooltip>
    </TooltipLoader>
  )
}

interface LoadingWheelProps {
  height?: number | string
  width?: number | string
  color?: string
}

export const LoadingWheel = ({
  height = 110,
  width = 110,
  color = palette.orange,
}: LoadingWheelProps) => (
  <ReactLoading
    type="spin"
    delay={0}
    color={color}
    height={height}
    width={width}
    data-testid="loading-wheel"
  />
)

interface LoadingResponsiveProps {
  size?: string | number
  offsetY?: string
  height?: string
  color?: string
}

export const LoadingResponsive = ({
  size = '25%',
  offsetY = '0%',
  height = '100%',
  color,
}: LoadingResponsiveProps) => (
  <ResponsiveWrapper height={height} role="status">
    <ResponsiveContainer size={size} offsetY={offsetY}>
      <LoadingWheel width={size} height={size} color={color} />
    </ResponsiveContainer>
  </ResponsiveWrapper>
)

interface LoadingProps {
  wheel?: {
    width?: number
    height?: number
  }
  showSeoTip?: boolean
  showText?: boolean
}

export default ({
  wheel,
  showSeoTip = true,
  showText = false,
  ...props
}: LoadingProps) => (
  <LoadingWrapper {...props}>
    {showSeoTip ? (
      <SeoTips />
    ) : (
      <>
        <LoadingWheel
          width={wheel?.width || 110}
          height={wheel?.height || 110}
        />
        {showText && <LoadingText />}
      </>
    )}
  </LoadingWrapper>
)
